// Placeholder skins (art-assets §4.2): used only when assets/skins/ holds no skin files. Twelve
// procedural skins drawn on a 512 px canvas in ink and palette colours, with a slightly jittered
// hand-drawn line. Areas left transparent show the wearer's cell colour (art-assets §4.1), and
// everything is clipped to the inscribed circle.

import type { Rgb } from '@ballio/shared';
import type { Canvas2D, Ctx2D } from '../../render/canvas.js';
import { cssAlpha } from '../../render/theme.js';
import {
  art,
  clipToCircle,
  drawFace,
  fillWith,
  INK,
  inkCirclePath,
  inkLinePath,
  inkPolyPath,
  paletteColor,
  strokeInk,
  TAU,
  WHITE,
} from './draw.js';

/** The twelve placeholder skin ids, in picker order. */
export const PLACEHOLDER_SKIN_IDS = [
  'stripes',
  'dots',
  'swirl',
  'checker',
  'smile',
  'star',
  'waves',
  'zigzag',
  'target',
  'plaid',
  'moon',
  'paw',
] as const;
export type PlaceholderSkinId = (typeof PLACEHOLDER_SKIN_IDS)[number];

export function isPlaceholderSkinId(id: string): id is PlaceholderSkinId {
  return (PLACEHOLDER_SKIN_IDS as readonly string[]).includes(id);
}

/** Display names for the picker. */
export const PLACEHOLDER_SKIN_NAMES: Readonly<Record<PlaceholderSkinId, string>> = Object.freeze({
  stripes: 'Stripes',
  dots: 'Polka',
  swirl: 'Swirl',
  checker: 'Checker',
  smile: 'Smiley',
  star: 'Star',
  waves: 'Waves',
  zigzag: 'Zigzag',
  target: 'Target',
  plaid: 'Plaid',
  moon: 'Moon',
  paw: 'Paw',
});

type Painter = (ctx: Ctx2D, s: number, seed: number) => void;

const LW = 14; // ink line width at 512 px (bold enough to read at small sizes)

function scaled(s: number, v: number): number {
  return (v * s) / 512;
}

const PAINTERS: Readonly<Record<PlaceholderSkinId, Painter>> = {
  stripes(ctx, s, seed) {
    const step = scaled(s, 88);
    const band = scaled(s, 40);
    const color = paletteColor(2);
    for (let k = -s; k < s * 2; k += step) {
      ctx.beginPath();
      inkPolyPath(ctx, [k, 0, k + band, 0, k + band - s, s, k - s, s], seed + k, 3, true);
      fillWith(ctx, color);
      ctx.beginPath();
      inkLinePath(ctx, k, 0, k - s, s, seed + k + 1, 3);
      inkLinePath(ctx, k + band, 0, k + band - s, s, seed + k + 2, 3);
      strokeInk(ctx, scaled(s, LW * 0.6));
    }
  },
  dots(ctx, s, seed) {
    const step = scaled(s, 112);
    const r = scaled(s, 30);
    let row = 0;
    for (let y = step / 2; y < s + r; y += step * 0.87, row++) {
      for (let x = row % 2 ? 0 : step / 2; x < s + r; x += step) {
        ctx.beginPath();
        inkCirclePath(ctx, x, y, r, seed + x * 7 + y, 0.06, 28);
        fillWith(ctx, paletteColor(row * 3 + Math.round(x / step)));
        strokeInk(ctx, scaled(s, LW * 0.5));
      }
    }
  },
  swirl(ctx, s, seed) {
    const c = s / 2;
    const pts: number[] = [];
    const turns = 3.2;
    for (let i = 0; i <= 160; i++) {
      const t = i / 160;
      const a = t * turns * TAU;
      const rr = t * s * 0.47;
      pts.push(c + rr * Math.cos(a), c + rr * Math.sin(a));
    }
    ctx.beginPath();
    inkPolyPath(ctx, pts, seed, scaled(s, 2));
    strokeInk(ctx, scaled(s, LW * 2.2), paletteColor(9));
    ctx.beginPath();
    inkPolyPath(ctx, pts, seed + 1, scaled(s, 2));
    strokeInk(ctx, scaled(s, LW * 0.6));
  },
  checker(ctx, s, seed) {
    const n = 6;
    const cell = s / n;
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        if ((i + j) % 2 !== 0) continue;
        const x = i * cell;
        const y = j * cell;
        ctx.beginPath();
        inkPolyPath(
          ctx,
          [x, y, x + cell, y, x + cell, y + cell, x, y + cell],
          seed + i * 13 + j,
          2.5,
          true,
        );
        fillWith(ctx, INK, 0.85);
      }
    }
  },
  smile(ctx, s, seed) {
    const r = s * 0.46;
    drawFace(ctx, s / 2, s / 2, r, 'grin', seed);
    // Rosy cheeks.
    for (const dx of [-0.52, 0.52]) {
      ctx.beginPath();
      ctx.ellipse(s / 2 + dx * r, s / 2 + r * 0.12, r * 0.12, r * 0.08, 0, 0, TAU);
      fillWith(ctx, paletteColor(11), 0.8);
    }
  },
  star(ctx, s, seed) {
    const c = s / 2;
    const pts: number[] = [];
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      const rr = i % 2 === 0 ? s * 0.4 : s * 0.17;
      pts.push(c + rr * Math.cos(a), c + rr * Math.sin(a) + s * 0.02);
    }
    ctx.beginPath();
    inkPolyPath(ctx, pts, seed, scaled(s, 4), true);
    fillWith(ctx, paletteColor(2));
    strokeInk(ctx, scaled(s, LW));
  },
  waves(ctx, s, seed) {
    const step = scaled(s, 80);
    let k = 0;
    for (let y = step / 2; y < s; y += step, k++) {
      const pts: number[] = [];
      for (let x = -10; x <= s + 10; x += scaled(s, 16)) {
        pts.push(x, y + Math.sin((x / s) * TAU * 2 + k) * scaled(s, 18));
      }
      ctx.beginPath();
      inkPolyPath(ctx, pts, seed + k, scaled(s, 1.5));
      strokeInk(ctx, scaled(s, LW * 1.4), k % 2 ? paletteColor(6) : WHITE);
      ctx.beginPath();
      inkPolyPath(ctx, pts, seed + k + 50, scaled(s, 1.5));
      strokeInk(ctx, scaled(s, LW * 0.45));
    }
  },
  zigzag(ctx, s, seed) {
    const step = scaled(s, 96);
    const tooth = scaled(s, 48);
    let k = 0;
    for (let y = step / 2; y < s + step; y += step, k++) {
      const pts: number[] = [];
      let up = true;
      for (let x = -tooth; x <= s + tooth; x += tooth) {
        pts.push(x, y + (up ? -tooth / 2 : tooth / 2));
        up = !up;
      }
      ctx.beginPath();
      inkPolyPath(ctx, pts, seed + k, scaled(s, 2));
      strokeInk(ctx, scaled(s, LW * 1.6), paletteColor(k * 5 + 1));
      ctx.beginPath();
      inkPolyPath(ctx, pts, seed + k + 30, scaled(s, 2));
      strokeInk(ctx, scaled(s, LW * 0.45));
    }
  },
  target(ctx, s, seed) {
    const c = s / 2;
    const rings = 5;
    for (let i = rings; i >= 1; i--) {
      const rr = (i / rings) * s * 0.5 - scaled(s, 8);
      ctx.beginPath();
      inkCirclePath(ctx, c, c, rr, seed + i, 0.02, 64);
      if (i % 2 === 1) fillWith(ctx, i === 1 ? paletteColor(0) : WHITE);
      strokeInk(ctx, scaled(s, LW * 0.6));
    }
  },
  plaid(ctx, s, seed) {
    const a: Rgb = paletteColor(0);
    const b: Rgb = paletteColor(7);
    const step = scaled(s, 128);
    for (let k = 0; k < s; k += step) {
      ctx.fillStyle = cssAlpha(a, 0.55);
      ctx.fillRect(k, 0, step * 0.45, s);
      ctx.fillStyle = cssAlpha(b, 0.5);
      ctx.fillRect(0, k + step * 0.5, s, step * 0.35);
    }
    for (let k = step * 0.72; k < s; k += step) {
      ctx.beginPath();
      inkLinePath(ctx, k, 0, k, s, seed + k, 2);
      inkLinePath(ctx, 0, k + step * 0.18, s, k + step * 0.18, seed + k + 9, 2);
      strokeInk(ctx, scaled(s, LW * 0.35));
    }
  },
  moon(ctx, s, seed) {
    const c = s / 2;
    const r = s * 0.4;
    // Crescent: the moon disc minus an offset disc, outlined.
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, r, 0, TAU);
    ctx.arc(c + r * 0.55, c - r * 0.25, r * 0.85, 0, TAU, true);
    fillWith(ctx, 0xf5e6a3);
    ctx.restore();
    ctx.beginPath();
    inkCirclePath(ctx, c, c, r, seed, 0.015, 72);
    strokeInk(ctx, scaled(s, LW * 0.7));
    ctx.beginPath();
    ctx.arc(c + r * 0.55, c - r * 0.25, r * 0.85, Math.PI * 0.62, Math.PI * 1.38);
    strokeInk(ctx, scaled(s, LW * 0.7));
    // A sleepy face on the crescent.
    const fx = c - r * 0.5;
    const fy = c + r * 0.1;
    ctx.beginPath();
    ctx.arc(fx, fy - r * 0.15, r * 0.09, 0, Math.PI);
    strokeInk(ctx, scaled(s, LW * 0.5));
    ctx.beginPath();
    ctx.arc(fx + r * 0.05, fy + r * 0.22, r * 0.14, Math.PI * 0.1, Math.PI * 0.9);
    strokeInk(ctx, scaled(s, LW * 0.5));
  },
  paw(ctx, s, seed) {
    const c = s / 2;
    ctx.beginPath();
    ctx.ellipse(c, c + s * 0.1, s * 0.17, s * 0.14, 0, 0, TAU);
    fillWith(ctx, INK);
    const toes: readonly [number, number][] = [
      [-0.21, -0.1],
      [-0.08, -0.22],
      [0.08, -0.22],
      [0.21, -0.1],
    ];
    toes.forEach(([dx, dy], i) => {
      ctx.beginPath();
      inkCirclePath(ctx, c + dx * s, c + dy * s, s * 0.065, seed + i, 0.05, 24);
      fillWith(ctx, INK);
    });
  },
};

/** Draws placeholder skin `id` at `size` px (default 512), or null for an unknown id. */
export function drawPlaceholderSkin(id: string, size = 512): Canvas2D | null {
  if (!isPlaceholderSkinId(id)) return null;
  const painter = PAINTERS[id];
  const seed = PLACEHOLDER_SKIN_IDS.indexOf(id) * 1000 + 17;
  return art(size, size, (ctx, w, h) => {
    ctx.save();
    clipToCircle(ctx, w, h);
    painter(ctx, Math.min(w, h), seed);
    ctx.restore();
  });
}
